import React, { useState, useEffect } from "react";
import { FileText, Download, Copy, Check, ExternalLink, Shield } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (isoString) => {
  if (!isoString) return "N/A";
  try {
    return new Date(isoString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  } catch {
    return isoString;
  }
};

const HashCopy = ({ hash }) => {
  const [copied, setCopied] = useState(false);
  
  const copyHash = async () => {
    await navigator.clipboard.writeText(hash);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <button
      onClick={copyHash}
      className="inline-flex items-center gap-1 text-xs font-mono bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
      title="Copy SHA-256 hash"
    >
      {hash.slice(0, 12)}...
      {copied ? (
        <Check className="w-3 h-3 text-green-600" />
      ) : (
        <Copy className="w-3 h-3 text-gray-500" />
      )}
    </button>
  );
};

const DocumentCard = ({ doc }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white hover:border-gray-300 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="p-2 bg-red-50 rounded-lg flex-shrink-0">
            <FileText className="w-5 h-5 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-gray-900 truncate" title={doc.title}>
              {doc.title}
            </h4>
            {doc.notes && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{doc.notes}</p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-500">
              <span>{formatFileSize(doc.file_size_bytes)}</span>
              <span>•</span>
              <span>Uploaded {formatDate(doc.uploaded_at)}</span>
              {doc.download_count > 0 && (
                <>
                  <span>•</span>
                  <span>{doc.download_count} downloads</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <a
            href={`${BACKEND_URL}${doc.download_url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded hover:bg-gray-700 transition-colors"
            data-testid={`download-doc-${doc.id}`}
          >
            <Download className="w-4 h-4" />
            View PDF
          </a>
          <a
            href={`${BACKEND_URL}${doc.download_url}?download=1`}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Download
          </a>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Shield className="w-3 h-3" />
          <span>SHA-256:</span>
          <HashCopy hash={doc.file_hash} />
        </div>
        {doc.public_visibility && (
          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
            Public
          </Badge>
        )}
      </div>
    </div>
  );
};

/**
 * PrimaryDocuments - Display attached PDF documents for an entity
 * 
 * @param {string} entityType - Type of entity (tickets, facts, claims, research)
 * @param {string} entityId - ID of the entity
 * @param {string} title - Optional custom title (default: "Primary Documents")
 */
export const PrimaryDocuments = ({ entityType, entityId, title = "Primary Documents" }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!entityType || !entityId) {
      setLoading(false);
      return;
    }
    
    api.get(`/media/entity/${entityType}/${entityId}`)
      .then(res => {
        setDocuments(res.data);
        setError(null);
      })
      .catch(err => {
        console.error("Failed to load documents", err);
        setError("Failed to load documents");
      })
      .finally(() => setLoading(false));
  }, [entityType, entityId]);
  
  // Don't render if no documents and not loading
  if (!loading && documents.length === 0) {
    return null;
  }
  
  return (
    <div className="archive-card p-6" data-testid="primary-documents-panel">
      <h3 className="text-lg font-semibold serif-heading mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5 text-red-600" />
        {title}
      </h3>
      
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <div className="space-y-3">
          {documents.map(doc => (
            <DocumentCard key={doc.id} doc={doc} />
          ))}
        </div>
      )}
      
      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          Documents are stored with cryptographic hashes for integrity verification.
          SHA-256 hashes can be verified against downloaded files.
        </p>
      </div>
    </div>
  );
};

export default PrimaryDocuments;
